import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import PaymentManager from '../../services/api/PaymentManager';
import { PaymentMethod } from '../../services/exports/Constants';
import * as stripeJs from '@stripe/stripe-js';
import { StripeConstructorOptions, loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import collect from 'collect.js';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import usePaymentFlow from '../../hooks/payment/usePaymentFlow';
import theme from '../../components-library/Theme';
import LoadingPage from '../loading/LoadingPage';

interface Props {
  children: any;
}

export interface PaymentCoordinatorContextInterface {
  loading: boolean;
  isStripePayment: boolean;
  paymentIntent: null | stripeJs.PaymentIntent;
  loadPaymentIntent: (forceCreate?: boolean) => Promise<void>;
}

export const PaymentCoordinatorContext: React.Context<PaymentCoordinatorContextInterface> = React.createContext(null);

export default function PaymentCoordinator(props: Props): ReactElement {
  const { children } = props;

  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const { data } = useOrderFlow();
  const { selectedPaymentMethod, hasOnlinePayments, isDirectPayment } = usePaymentFlow();

  const [paymentIntent, setPaymentIntent] = useState(null);
  const [loading, setLoading] = useState(false);
  const isStripePayment =
    Boolean(selectedPaymentMethod) && selectedPaymentMethod !== PaymentMethod.Cash && data?.total > 0;

  const stripePromise = useMemo<null | Promise<stripeJs.Stripe>>(
    () =>
      !company?.service_provider?.stripe_key
        ? null
        : loadStripe(
            company?.service_provider?.stripe_key,
            collect()
              .when(isDirectPayment, (options) => options.put('stripeAccount', company?.stripe_id))
              .all() as StripeConstructorOptions,
          ),
    [company?.service_provider?.stripe_key, company?.stripe_id, isDirectPayment],
  );

  const loadPaymentIntent = useCallback(
    async (forceCreate: boolean = false) => {
      if (loading) {
        return;
      }

      setLoading(true);

      const body = {
        company_id: company.id,
        payment_method_id: selectedPaymentMethod,
      };

      const { response, success } =
        paymentIntent?.id && !forceCreate
          ? await PaymentManager.updatePaymentIntent(paymentIntent.id, body)
          : await PaymentManager.createPaymentIntent(body);

      if (success) {
        setPaymentIntent(response.data);
      }

      setTimeout(() => setLoading(false), 5);
    },
    [loading, company?.id, data.total, paymentIntent?.id, selectedPaymentMethod],
  );

  useEffect(() => {
    hasOnlinePayments && stripePromise && loadPaymentIntent(true);
  }, [stripePromise, selectedPaymentMethod]);

  const context = useMemo(
    () => ({
      loading,
      isStripePayment,
      paymentIntent,
      loadPaymentIntent,
    }),
    [loading, isStripePayment, paymentIntent, loadPaymentIntent],
  );

  return (
    <PaymentCoordinatorContext.Provider value={context}>
      {!isStripePayment && children}
      {isStripePayment && (
        <>
          {stripePromise && paymentIntent ? (
            <Elements
              key={`company-${company?.stripe_id}-${isDirectPayment}-${paymentIntent?.client_secret}`}
              stripe={stripePromise}
              options={{
                clientSecret: paymentIntent?.client_secret,
                appearance: {
                  variables: {
                    colorText: theme.color.black.DEFAULT,
                  },
                },
              }}
            >
              {children}
            </Elements>
          ) : (
            <LoadingPage />
          )}
        </>
      )}
    </PaymentCoordinatorContext.Provider>
  );
}
